import { useEffect, useState } from 'react';
import { Link2, Loader2, Lock, PenLine } from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { DepositorIdentity } from '@/lib/agreements/api';
import {
    canCreateDepositAgreement,
    depositorAddressError,
    depositorNameError,
    depositorPhoneError,
    type DepositorDraft,
} from './depositAgreement';

interface CreateDepositAgreementDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Whether the deposit belongs to a contract yet — it changes the copy, not the rule. */
    attachedToContract: boolean;
    /** The contract driver's details, when the deposit has a contract. Blank otherwise. */
    prefill: DepositorDraft;
    /** Amount and date, so the admin can see which deposit they are papering. */
    depositSummary: string;
    isPending: boolean;
    onConfirm: (depositor: DepositorIdentity) => void;
}

/**
 * Generate the deposit agreement, and name the person who will sign it.
 *
 * THE NAME, PHONE AND ADDRESS ARE THE IDENTITY, NOT LABELS. A deposit paid before a vehicle
 * was allocated has no contract, no driver row and no account behind it, so there is nothing
 * for the signature to point at — what is typed here is frozen onto the depositor's signature
 * row at create, printed on the document, and is what the executed PDF attests to. It is worth
 * getting right before the link goes out: the only way to correct it afterwards is to void
 * the document and reissue.
 *
 * THE NAME AND PHONE ARE ASKED FOR EVEN WITH A CONTRACT IN HAND, AND THAT IS THIS FORM'S RULE
 * RATHER THAN THE RPC'S. `agreement_create` insists on a name only where the deposit has no
 * contract, and never on the phone — but the deposit template names `driver.full_name` and
 * `driver.phone` as required merge fields, so a document created without them is accepted and
 * then cannot be sent. Where a contract driver is known both arrive as a prefill and stay
 * editable, because the person who actually paid is not always the person the contract ends
 * up naming.
 *
 * THE ADDRESS IS NEVER PREFILLED, AND IT IS DEMANDED ONLY WHERE IT IS THE ONLY SOURCE — which
 * is this form's one departure from asking for everything. It is a manual capture with no
 * profile-derived source, so it starts blank on every deposit and an admin states where the
 * person who PAID lives rather than confirming a line the form filled in. On a deposit that
 * already has a contract the RPC DISCARDS it and `driver.address` resolves from that driver's
 * profile, so demanding it there would be typing that changes nothing; on one with no contract
 * there is no profile and no other record of it. That is the same boundary `agreement_create`
 * itself draws when it insists on a name.
 */
export default function CreateDepositAgreementDialog({
    open,
    onOpenChange,
    attachedToContract,
    prefill,
    depositSummary,
    isPending,
    onConfirm,
}: CreateDepositAgreementDialogProps) {
    const [draft, setDraft] = useState<DepositorDraft>({ name: '', phone: '', address: '' });
    const [touched, setTouched] = useState<{ name: boolean; phone: boolean; address: boolean }>({
        name: false,
        phone: false,
        address: false,
    });

    useEffect(() => {
        if (open) {
            setDraft({ name: prefill.name, phone: prefill.phone, address: prefill.address });
            setTouched({ name: false, phone: false, address: false });
        }
    }, [open, prefill.name, prefill.phone, prefill.address]);

    const nameError = depositorNameError(draft.name);
    const phoneError = depositorPhoneError(draft.phone);
    // The RPC discards a typed address wherever the deposit's contract names a driver with an
    // account, so it is only demanded where nothing else can answer for it.
    const addressRequired = !attachedToContract;
    const addressError = depositorAddressError(draft.address, addressRequired);
    const ready = canCreateDepositAgreement(draft, addressRequired);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Generate the deposit agreement</DialogTitle>
                    <DialogDescription>
                        {depositSummary} — the wording is generated from the current figures when you
                        confirm.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label
                            htmlFor="deposit-depositor-name"
                            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                        >
                            Depositor full name *
                        </Label>
                        <Input
                            id="deposit-depositor-name"
                            value={draft.name}
                            onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                            onBlur={() => setTouched((current) => ({ ...current, name: true }))}
                            placeholder="As it should appear on the agreement"
                            autoComplete="off"
                            aria-invalid={touched.name && !!nameError}
                            className={cn(touched.name && nameError && 'border-destructive')}
                        />
                        {touched.name && nameError ? (
                            <p className="text-xs text-destructive">{nameError}</p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                {attachedToContract
                                    ? 'Taken from the contract driver. Change it if somebody else paid — the ' +
                                      'agreement is addressed to whoever is named here.'
                                    : 'This deposit is not attached to a contract, so this name is the only ' +
                                      'record of who paid it. Correcting it later means voiding the document.'}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label
                            htmlFor="deposit-depositor-phone"
                            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                        >
                            Depositor phone *
                        </Label>
                        <Input
                            id="deposit-depositor-phone"
                            value={draft.phone}
                            onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))}
                            onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
                            placeholder="e.g. 024 000 0000"
                            inputMode="tel"
                            autoComplete="off"
                            aria-invalid={touched.phone && !!phoneError}
                            className={cn(touched.phone && phoneError && 'border-destructive')}
                        />
                        {touched.phone && phoneError ? (
                            <p className="text-xs text-destructive">{phoneError}</p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Printed on the agreement beside the name, and frozen with it.
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label
                            htmlFor="deposit-depositor-address"
                            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                        >
                            Depositor address{addressRequired ? ' *' : ''}
                        </Label>
                        <Input
                            id="deposit-depositor-address"
                            value={draft.address}
                            onChange={(event) =>
                                setDraft((current) => ({ ...current, address: event.target.value }))
                            }
                            onBlur={() => setTouched((current) => ({ ...current, address: true }))}
                            placeholder="House number and street, or a digital address"
                            autoComplete="off"
                            aria-invalid={touched.address && !!addressError}
                            className={cn(touched.address && addressError && 'border-destructive')}
                        />
                        {touched.address && addressError ? (
                            <p className="text-xs text-destructive">{addressError}</p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                {addressRequired
                                    ? 'This deposit is not attached to a contract, so nothing else on ' +
                                      'file records where the person who paid lives. The agreement ' +
                                      'states it.'
                                    : 'Optional — the contract driver’s own address is what the ' +
                                      'agreement states. Fill this in only if somebody else paid.'}
                            </p>
                        )}
                    </div>
                </div>

                <ul className="space-y-3 border-t pt-4 text-sm">
                    <li className="flex gap-3">
                        <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            The depositor signs through a link you copy from this sheet and share with
                            them. No account is needed, and nothing is emailed.
                        </span>
                    </li>
                    <li className="flex gap-3">
                        <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>Primelane countersigns here afterwards — the company always signs last.</span>
                    </li>
                    <li className="flex gap-3">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            The wording freezes the moment the depositor signs. Changing a figure after
                            that needs a void and reissue.
                        </span>
                    </li>
                </ul>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setTouched({ name: true, phone: true, address: true });
                            if (!ready) return;
                            onConfirm({
                                name: draft.name.trim(),
                                phone: draft.phone.trim(),
                                address: draft.address.trim(),
                            });
                        }}
                        disabled={isPending}
                    >
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Generate agreement
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
